import React, { useMemo } from "react";
import Chart from "react-apexcharts";
import { Box, Typography } from "@mui/material";
import StandardLoadingIndicator from "components/StandardLoadingIndicator";
import { parseISO, differenceInDays } from "date-fns";
import { IoAnalyticsOutline } from "react-icons/io5";
import GlassCard from "components/GlassCard";
import { GRADIENTS } from "utils/designTokens";

const MAX_YEARS = 5; // only show trials completing within 5 years

// ---------------------------------------------------------------------------
// Status colors (shared palette)
// ---------------------------------------------------------------------------
const STATUS_COLORS = {
  RECRUITING: "#10B981",
  ACTIVE: "#3B82F6",
  NOT_YET_RECRUITING: "#60A5FA",
  COMPLETED: "#6B7280",
};

const getStatusColor = (status) => STATUS_COLORS[status] || "#6B7280";

// ---------------------------------------------------------------------------
// Date parser
// ---------------------------------------------------------------------------
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  if (/^\d{4}-\d{2}$/.test(dateStr)) {
    return parseISO(dateStr + "-01");
  }
  return parseISO(dateStr);
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const ThreatMatrix = ({ trials, loading = false }) => {
  const { series, chartOptions } = useMemo(() => {
    if (!trials || trials.length === 0) {
      return { series: [], chartOptions: {} };
    }

    const now = new Date();

    // Collect raw data — cap at MAX_YEARS, log-scale bubbles
    const rawPoints = [];
    trials.forEach((trial) => {
      if (trial.status === "COMPLETED") return; // exclude completed trials
      const completionDate = parseDate(trial.completion_date);
      if (!completionDate || isNaN(completionDate.getTime())) return;
      const daysToCompletion = differenceInDays(completionDate, now);
      const yearsToCompletion = Math.max(0, daysToCompletion / 365.25);
      if (yearsToCompletion > MAX_YEARS) return; // skip trials beyond 5-year horizon
      rawPoints.push({
        yearsToCompletion: Math.round(yearsToCompletion * 100) / 100,
        enrollment: trial.enrollment || 1,
        nctId: trial.nct_id,
        officialTitle: trial.official_title || "",
        sponsor: trial.sponsor || "",
        status: trial.status || "UNKNOWN",
        completionDate: trial.completion_date,
      });
    });

    if (rawPoints.length === 0) return { series: [], chartOptions: {} };

    // Log-scale the bubble z so outliers don't dominate (z range: 5–60)
    const maxEnrollment = Math.max(...rawPoints.map((p) => p.enrollment), 1);
    const logMax = Math.log10(maxEnrollment + 1);
    const scaleZ = (enrollment) => {
      const logVal = Math.log10(enrollment + 1);
      return Math.round(5 + (logVal / logMax) * 55);
    };

    // Group trials by status for separate series (different colors)
    const statusGroups = {};
    rawPoints.forEach((p) => {
      if (!statusGroups[p.status]) statusGroups[p.status] = [];
      statusGroups[p.status].push({
        x: p.yearsToCompletion,
        y: p.enrollment,
        z: scaleZ(p.enrollment),
        nctId: p.nctId,
        officialTitle: p.officialTitle,
        sponsor: p.sponsor,
        status: p.status,
        completionDate: p.completionDate,
      });
    });

    const seriesData = Object.entries(statusGroups).map(([status, data]) => ({
      name: status.replace(/_/g, " "),
      data,
      color: getStatusColor(status),
    }));

    // Compute axis ranges for quadrant annotation
    const allEnrollments = seriesData.flatMap((s) => s.data.map((d) => d.y));
    const maxEnrollmentForAxis = Math.max(...allEnrollments, 10);
    const midYears = MAX_YEARS / 2;
    const midEnrollment = maxEnrollmentForAxis / 2;

    const options = {
      chart: {
        type: "bubble",
        background: "transparent",
        toolbar: { show: false },
        fontFamily: "inherit",
        zoom: { enabled: false },
      },
      colors: seriesData.map((s) => s.color),
      fill: { opacity: 0.7 },
      stroke: { width: 1, colors: ["rgba(255,255,255,0.2)"] },
      xaxis: {
        title: {
          text: "Years to Completion",
          style: { color: "rgba(255,255,255,0.55)", fontSize: "12px", fontWeight: 600 },
        },
        labels: {
          style: { colors: "rgba(255,255,255,0.5)", fontSize: "11px" },
          formatter: (val) => {
            const num = Number(val);
            return num < 0 ? "" : `${num.toFixed(1)}y`;
          },
        },
        tickAmount: 6,
        min: -0.3,
        max: MAX_YEARS + 0.2,
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        title: {
          text: "Trial Size (Enrollment)",
          style: { color: "rgba(255,255,255,0.55)", fontSize: "12px", fontWeight: 600 },
        },
        labels: {
          style: { colors: "rgba(255,255,255,0.5)", fontSize: "11px" },
          formatter: (val) => (val < 0 ? "" : Math.round(val).toString()),
        },
        min: -(maxEnrollmentForAxis * 0.08),
        max: maxEnrollmentForAxis * 1.15,
      },
      grid: {
        borderColor: "rgba(255,255,255,0.06)",
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: true } },
      },
      annotations: {
        // "Imminent Threats" zone: top-left quadrant
        xaxis: [
          {
            x: midYears,
            borderColor: "rgba(255,255,255,0.08)",
            strokeDashArray: 6,
          },
        ],
        yaxis: [
          {
            y: midEnrollment,
            borderColor: "rgba(255,255,255,0.08)",
            strokeDashArray: 6,
          },
        ],
        points: [
          {
            x: MAX_YEARS * 0.12,
            y: maxEnrollmentForAxis * 0.92,
            marker: { size: 0 },
            label: {
              text: "IMMINENT THREATS",
              borderWidth: 0,
              style: {
                color: "#EF4444",
                background: "transparent",
                fontSize: "10px",
                fontWeight: 700,
                padding: { left: 0, right: 0, top: 0, bottom: 0 },
              },
            },
          },
        ],
      },
      tooltip: {
        custom: ({ seriesIndex, dataPointIndex, w }) => {
          const point = w.config.series[seriesIndex]?.data[dataPointIndex];
          if (!point) return "";
          return `
            <div style="padding:10px 14px;background:#1A1E3C;border:1px solid rgba(255,255,255,0.1);border-radius:8px;font-size:12px;color:#fff;max-width:340px;word-break:break-word;white-space:normal;">
              <div style="font-weight:700;margin-bottom:4px;">${point.nctId}</div>
              <div style="color:rgba(255,255,255,0.7);margin-bottom:6px;line-height:1.5;">${point.officialTitle}</div>
              ${point.sponsor ? `<div style="color:rgba(255,255,255,0.5);margin-bottom:6px;">Sponsor: ${point.sponsor}</div>` : ""}
              <div style="display:flex;gap:14px;flex-wrap:wrap;">
                <span>Enrollment: <b>${point.y}</b></span>
                <span>Years left: <b>${point.x.toFixed(1)}</b></span>
              </div>
              <div style="color:${getStatusColor(point.status)};font-weight:600;margin-top:4px;">
                ${point.status.replace(/_/g, " ")}
              </div>
            </div>
          `;
        },
      },
      legend: {
        position: "top",
        horizontalAlign: "right",
        labels: { colors: "rgba(255,255,255,0.6)" },
        fontSize: "12px",
        markers: { radius: 3 },
        itemMargin: { horizontal: 10 },
      },
      dataLabels: { enabled: false },
    };

    return { series: seriesData, chartOptions: options };
  }, [trials]);

  const hasData = series && series.length > 0;
  if (!loading && (!trials?.length || !hasData)) return null;

  return (
    <GlassCard sx={{ overflow: "visible" }}>
      <Box p={3} sx={{ position: "relative", zIndex: 1, overflow: "visible" }}>
        {/* Section header */}
        <Box display="flex" alignItems="center" mb={0.5}>
          <IoAnalyticsOutline size={20} color="#A78BFA" />
          <Typography
            variant="h6"
            sx={{
              background: GRADIENTS.chartAccent,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontWeight: 700,
              fontSize: "1.2rem",
              ml: 1,
            }}
          >
            Most Imminent Threats
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.8rem", mb: 2 }}
        >
          Large bubbles ending soon (top-left) represent imminent competitive threats
        </Typography>

        {/* Loading skeleton — matches other pages */}
        {loading ? (
          <StandardLoadingIndicator containerHeight={380} />
        ) : (
          /* Chart — overflow:visible so tooltip is never clipped by GlassCard */
          <Box
            sx={{
              "& .apexcharts-canvas": { overflow: "visible !important" },
              "& .apexcharts-svg":    { overflow: "visible !important" },
            }}
          >
            <Chart
              options={chartOptions}
              series={series}
              type="bubble"
              height={380}
            />
          </Box>
        )}
      </Box>
    </GlassCard>
  );
};

export default ThreatMatrix;
